import AlertCircle from '@mdi/svg/svg/alert-circle.svg?react';
import Alert from '@mdi/svg/svg/alert.svg?react';
import Info from '@mdi/svg/svg/information.svg?react';
import TimerSandEmpty from '@mdi/svg/svg/timer-sand-empty.svg?react';
import {
  type CallhomeCamera,
  type CallhomeStream,
  type CHCameraId,
  type VMSCameraId,
} from '@solinkcorp/api-types';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { Button, MDIIcon, toast } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import { type EventCamera, type IEvent } from 'clients/PlatformApi';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import S3Image from 'components/S3Image/S3Image';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React, { PureComponent, useContext, useMemo } from 'react';
import { TransitionGroup as CSSTransitionGroup } from 'react-transition-group';
import { findCamera } from 'src/hooks/findCamera';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import { getEarliestSegmentDate, getLongestRetention } from 'utils/CameraUtil';
import * as EventUtil from 'utils/EventUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import { type CamerasDictionary, type VideoDiscoverySite } from 'utils/types';
import * as constants from 'vd-constants';
import QuickPlayer from '../VideoPlayer/components/QuickPlayer/QuickPlayer';
import './EventQuickViewer.sass';
import FaceblurOverlay from './FaceblurOverlay';

interface TargetCamera {
  location?: VideoDiscoverySite;
  eventCamera: EventCamera | undefined;
  vmsCameraId: VMSCameraId | undefined;
  callhomeCamera: CallhomeCamera | undefined;
  chCameraId?: CHCameraId | undefined;
  warning: string | undefined;
}

interface EventQuickViewerProps {
  bucketPrefix?: string;
  camIdx: number;
  camerasDictionary: CamerasDictionary;
  event: IEvent;
  investigate?: (vmsCameraId?: VMSCameraId) => void;
  key?: string;
  locale: LocaleDefinition;
  masterLocationsList: VideoDiscoverySite[];
  eventCamerasLength: number;
  width: number;
  onPlayEvent?: (vmsCameraId?: VMSCameraId) => void;
  images?: IEvent['cameras'][0]['images'];
  hasMultipleLocationCameras?: boolean;
  targetCamera: TargetCamera;
  active?: boolean;
}

interface EventQuickViewerState {
  eventVideoInCloudOrLocalRetention: boolean;
  imageProcessing: boolean;
  playable: boolean;
  showProcessingMsg: boolean;
  start: boolean;
  videoNearRetentionLimit: boolean;
  warning?: string;
}

class EventQuickViewer extends PureComponent<EventQuickViewerProps, EventQuickViewerState> {
  private _isMounted: boolean;
  private playTimeout?: NodeJS.Timeout;

  constructor(props: EventQuickViewerProps) {
    super(props);

    this._isMounted = false;

    this.state = {
      eventVideoInCloudOrLocalRetention: true,
      imageProcessing: false,
      playable: false,
      showProcessingMsg: false,
      start: false,
      videoNearRetentionLimit: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.updateVideoRetentionStatus();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate() {
    this.updateVideoRetentionStatus();
  }

  /**
   * This function updates two state properties:
   * 1) `eventVideoInCloudOrLocalRetention` and
   * 2) `videoNearRetentionLimit`
   */
  updateVideoRetentionStatus = () => {
    const {
      camerasDictionary, // Key'd by the unique Callhome Camera ID
      event,
      event: { startTime },
      masterLocationsList,
      targetCamera,
    } = this.props;

    if (isEmpty(camerasDictionary) && isEmpty(masterLocationsList)) {
      // Cannot update video retention status without cameras dictionary and master locations list
      // When these two properties update, we'll re-trigger this function.
      return;
    }

    const { eventCamera, callhomeCamera } = targetCamera || {};

    if (!callhomeCamera) {
      // No camera was found...
      // TODO Improvement
      // Sometimes cameras are deleted since the event was created, if we could handle that
      // it would be better but for now we'll do nothing special for that case.
      // No camera found, use default values for `eventVideoInCloudOrLocalRetention` and
      // `videoNearRetentionLimit`
      console.warn(
        `Could not find matching camera for ID: ${eventCamera?.id} and Name: ${eventCamera?.name}` +
          `this may be expected if the camera is new. Please try again after a little while...`,
      );
      this.setState({
        eventVideoInCloudOrLocalRetention: true,
        videoNearRetentionLimit: false,
      });
    } else {
      let eventVideoInCloudOrLocalRetention;
      // Found a camera...
      const earliestSegmentDate = getEarliestSegmentDate(callhomeCamera);
      if (earliestSegmentDate === undefined) {
        console.warn(`Camera ${callhomeCamera.name} does not have an earliest segment date.`);
        // Without an earliest segment date, we cannot use this flag to accurately to display any information.
        eventVideoInCloudOrLocalRetention = true;
      } else {
        eventVideoInCloudOrLocalRetention =
          EventUtil.isEventInCloud(event) ||
          moment(startTime).isSameOrAfter(moment(earliestSegmentDate));
      }

      const retentionLimit = moment()
        .startOf('day')
        .subtract(getLongestRetention({ camera: callhomeCamera }), 'days');
      const hoursBetweenStartTimeAndRetentionLimit = moment(startTime).diff(
        moment(retentionLimit),
        'hours',
        true,
      );
      const videoNearRetentionLimit =
        moment(startTime).isSameOrAfter(retentionLimit) &&
        hoursBetweenStartTimeAndRetentionLimit <= constants.WARNING_HOURS_AFTER_LOCAL_RETENTION &&
        retentionLimit.valueOf() !== 0;

      this.setState({
        eventVideoInCloudOrLocalRetention,
        videoNearRetentionLimit,
      });
    }
  };

  render() {
    const {
      active,
      event,
      locale,
      images,
      eventCamerasLength,
      targetCamera,
      hasMultipleLocationCameras,
    } = this.props;

    const {
      imageProcessing,
      playable,
      showProcessingMsg,
      start,
      eventVideoInCloudOrLocalRetention,
      videoNearRetentionLimit,
      warning,
    } = this.state;

    const { eventCamera, callhomeCamera, location, warning: cameraWarning } = targetCamera || {};
    const imgUrl = eventCamera && encodeURIComponent(eventCamera.thumbnail);
    const checkInUrl = callhomeCamera?.thumbnail;

    const overlayClass = classNames('overlay', { shadow: start && playable });
    const warningClass = classNames('warning', { show: warning });
    const imgWrapClass = classNames('img-wrap', { 'show-s3-img': !(start && !active) });
    const mainClass = classNames('event-quick-viewer', {
      'quick-footer-message': !eventVideoInCloudOrLocalRetention || videoNearRetentionLimit,
      'cursor-not-allowed': !eventVideoInCloudOrLocalRetention || !callhomeCamera,
    });

    const camNameClass = classNames('cam-name', { hidden: start });

    const shouldShowThumbnailOverlay = cameraWarning; // To prevent Cypress clicking 'through' the top div and click on the overlay
    const useCheckInThumbnail =
      EventUtil.isScheduledActivity(event) && !EventUtil.shouldThumbnailReady(event) && checkInUrl;

    const faceblur = !!event.restrictions?.faceblur;

    // To suppress cycling of multiple images of different resolutions for Event Spot Check events
    const isEventSpotCheck = EventUtil.isEventSpotCheckEvent(event);

    return (
      <div
        id={`qp-${event.id}`}
        className={mainClass}
        data-testid="events-quick-viewer-image"
        onClick={
          eventVideoInCloudOrLocalRetention && callhomeCamera ? this._investigate : undefined
        }
        style={{ width: this.props.width ?? 320, height: ((this.props.width ?? 320) / 16) * 9 }}
      >
        <div className={camNameClass}>
          <label>{(eventCamera || callhomeCamera)?.name}</label>
        </div>
        {!eventVideoInCloudOrLocalRetention && (
          <>
            <div className="quick-footer-message">
              <MDIIcon icon={Info} />
              <span>{locale.camera.videoOutOfRetention}</span>
            </div>
            {callhomeCamera && !isEmpty(callhomeCamera.streams) && (
              <div className="retention-messages-wrapper">
                {callhomeCamera.streams.map((stream: CallhomeStream) => (
                  <div key={stream.id} className="retention-message">
                    <span>
                      {stream.name} {locale.camera.retention}: {stream.retention}{' '}
                      {locale.general.days}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {eventVideoInCloudOrLocalRetention && videoNearRetentionLimit && (
          <div className="quick-footer-message">
            <MDIIcon icon={Alert} />
            <span>{locale.camera.videoNearRetentionLimit}</span>
          </div>
        )}
        {imageProcessing && (
          <div
            className="processing-icon-container"
            onMouseEnter={this._toggleProcessingMsg.bind(this, true)}
            onMouseLeave={this._toggleProcessingMsg.bind(this, false)}
          >
            <MDIIcon icon={TimerSandEmpty} size={16} fill="var(--color-player-yellow)" />
          </div>
        )}
        <div className="processing-transition-container">
          <CSSTransitionGroup
            className="processing-msg"
            appear={true}
            timeout={{ exit: 300, enter: 500 }}
          >
            {showProcessingMsg && (
              <div className="processing-msg-container" key="processingMsgContainer">
                <div>{locale.event.thumbnailProcessing}</div>
              </div>
            )}
          </CSSTransitionGroup>
        </div>
        {eventVideoInCloudOrLocalRetention &&
          eventCamerasLength > 1 &&
          !hasMultipleLocationCameras && (
            <div className="play-all-button" key="play-all-button">
              <Button
                isPrimary={false}
                colorMode={'light'}
                size="small"
                text={`${locale.videoPlayerOptions.playAll} (${eventCamerasLength})`}
              />
            </div>
          )}
        <div
          className="event-quick-play-container"
          onMouseEnter={this._play}
          onMouseLeave={this._stop}
        >
          <div className={imgWrapClass}>
            <S3Image
              blur={imageProcessing}
              id={callhomeCamera?.id ?? eventCamera?.id ?? ''}
              images={!isEventSpotCheck ? images : undefined}
              name={callhomeCamera?.name ?? eventCamera?.name}
              url={useCheckInThumbnail ? checkInUrl : imgUrl}
              checkInUrl={checkInUrl}
              updateImageProcessingState={this._updateImageProcessingState}
            />
          </div>
          {faceblur && <FaceblurOverlay hidden={start} />}
          {shouldShowThumbnailOverlay && (
            <div className={overlayClass}>
              <div className={warningClass}>
                <MDIIcon icon={AlertCircle} size={16} />
                {cameraWarning}
              </div>
            </div>
          )}
          {start && !active && callhomeCamera && location && (
            <QuickPlayer
              camera={callhomeCamera}
              location={location}
              start={event.startTime.valueOf()}
              end={event.endTime.valueOf()}
              vtt
            />
          )}
        </div>
      </div>
    );
  }

  _updateImageProcessingState = (value: boolean) => this.setState({ imageProcessing: value });

  _toggleProcessingMsg = (value: boolean) => this.setState({ showProcessingMsg: value });

  _investigate = (event: React.MouseEvent<HTMLDivElement>) => {
    const { onPlayEvent, targetCamera } = this.props;

    // play all events when play-all-button button is clicked
    // can't register onClick on button.view-all due to event bubling - onClick on div.event-quick-viewer
    const shouldPlayAll =
      event.target instanceof HTMLButtonElement ||
      (event.target as HTMLElement).parentNode instanceof HTMLButtonElement;
    if (shouldPlayAll) {
      onPlayEvent?.();
      return;
    }

    const { vmsCameraId, warning: cameraWarning } = targetCamera || {};
    if (cameraWarning) {
      toast.error(cameraWarning);
      return;
    }

    onPlayEvent?.(vmsCameraId);
  };

  _stop = () => {
    if (!this.state.eventVideoInCloudOrLocalRetention || FeaturesUtil.hasNoFullAccessInKiosk()) {
      return;
    }

    if (this.playTimeout) {
      clearTimeout(this.playTimeout);
    }

    this.setState({
      start: false,
    });
  };

  _play = () => {
    const { targetCamera } = this.props;
    const { warning } = this.state;

    if (
      !this.state.eventVideoInCloudOrLocalRetention ||
      FeaturesUtil.hasNoFullAccessInKiosk() ||
      warning ||
      !targetCamera?.callhomeCamera
    ) {
      return;
    }

    this.playTimeout = setTimeout(() => {
      if (!this._isMounted) {
        return;
      }

      this.setState({
        playable: true,
        start: true,
      });
    }, 400);
  };
}

export default function ComposedEventQuickViewer(props: EventQuickViewerProps) {
  const { event, camIdx } = props;
  const camerasDictionary = useFluxStore(LocationStore, (store) =>
    store.getAllLocationCamerasDict(),
  );
  const locale = useLocale();
  const { masterLocationsList } = useContext(CommonContext);

  const targetCamera = useMemo(
    () => findCamera(locale, masterLocationsList, camerasDictionary, event, camIdx),
    [event, camIdx, locale, masterLocationsList, camerasDictionary],
  );

  const hasMultipleLocationCameras = useMemo(() => {
    return EventUtil.doesEventHaveMultipleLocationCameras(event, camerasDictionary);
  }, [event, camerasDictionary]);

  return (
    <EventQuickViewer
      {...props}
      locale={locale}
      camerasDictionary={camerasDictionary}
      masterLocationsList={masterLocationsList}
      targetCamera={targetCamera as TargetCamera}
      hasMultipleLocationCameras={hasMultipleLocationCameras}
    />
  );
}
